import React, { useState, useEffect, useMemo } from 'react';
import { Button, Card, CardBody, Col, Container, Row, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb2';
import TableContainer from '../../components/Common/DataTableContainer';
import axiosInstance from "../Utility/axiosInstance";
import { showError, showSuccess } from '../../helpers/notification_helper';
import { ClipLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { MdDeleteOutline } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import Select from 'react-select';

const ReasonsMasterList = () => {
    const [statuses, setStatuses] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editReasonId, setEditReasonId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [newReason, setNewReason] = useState('');

    // Delete Confirmation states
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState('');
    const [confirmAction, setConfirmAction] = useState(false);

    const navigate = useNavigate();

    const permissions = JSON.parse(localStorage?.getItem('permissions') || '[]');
    const permissionNames = permissions.map(permission => permission?.permissionName);

    const hasAddPermission = permissionNames?.includes("Reason Add");
    const hasEditPermission = permissionNames?.includes("Reason Edit");
    const hasDeletePermission = permissionNames?.includes("Reason Delete");

    useEffect(() => {
        if (!permissionNames?.includes("Reason List")) {
            navigate('/pages-403');
        }
    }, [permissionNames, navigate]);

    // Fetch initial predefined statuses from API
    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const response = await axiosInstance.get('', { params: { sp: "usp_WorkOrderStatuses" } });
                const rawData = response?.data?.Data || response?.data || [];

                if (Array.isArray(rawData)) {
                    const formattedStatuses = rawData.map(status => {
                        // Handle case where status might be a string or an object
                        let label = "";
                        let value = "";

                        if (typeof status === 'string') {
                            label = status;
                            value = status;
                        } else if (typeof status === 'object' && status !== null) {
                            // Use stateName as label and statekey as value as requested
                            label = status.stateName || status.StatusCategory || status.Status_Category || status.statusName || status.name || status.status || JSON.stringify(status);
                            value = status.stateKey || status.StatusCategory || status.Status_Category || status.statusName || status.name || status.status || status.id || label;
                        }

                        return { value, label };
                    });
                    setStatuses(formattedStatuses);
                }
            } catch (error) {
                console.error("Failed to fetch statuses", error);
            }
        };
        fetchStatuses();
    }, []);

    const fetchReasons = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('', {
                params: {
                    sp: "usp_GetWorkordersStatusReasons"
                }
            });
            if (response && response.data && response.data.Data) {
                setReasons(response.data.Data);
            } else {
                setReasons([]);
            }
        } catch (error) {
            console.error("Failed to fetch reasons", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReasons();
    }, []);

    const handleOpenModal = (reason = null) => {
        if (reason) {
            setIsEditMode(true);
            setEditReasonId(reason.ReasonID || reason.ReasonId || reason.reasonId || reason.id);
            const statusValue = reason.StatusCategory || '';
            setSelectedStatus(statuses.find(s => s.value === statusValue) || { value: statusValue, label: statusValue });
            setNewReason(reason.ReasonText || '');
        } else {
            setIsEditMode(false);
            setEditReasonId(null);
            setSelectedStatus(null);
            setNewReason('');
        }
        setModalOpen(true);
    };

    const handleSaveReason = async () => {
        if (!selectedStatus) {
            showError("Please select a status.");
            return;
        }
        if (!newReason.trim()) {
            showError("Reason text cannot be empty.");
            return;
        }

        try {
            if (isEditMode) {
                const response = await axiosInstance.post('', {
                    sp: "usp_UpdateWorkOrderStatusReason",
                    ReasonID: editReasonId,
                    StatusCategory: selectedStatus?.value,
                    ReasonText: newReason
                });
                if (response) {
                    showSuccess("Reason updated successfully.");
                }
            } else {
                console.log("selectedStatus", selectedStatus)
                const response = await axiosInstance.post('', {
                    sp: "usp_InsertWorkOrderStatusReason",
                    StatusCategory: selectedStatus?.value,
                    ReasonText: newReason
                });
                if (response) {
                    showSuccess("Reason added successfully.");
                }
            }

            setModalOpen(false);
            fetchReasons();

        } catch (error) {
            console.error("Failed to save reason", error);
        }
    };

    const handleDelete = async (reasonId) => {
        try {
            const response = await axiosInstance.post('', {
                sp: "usp_DeleteWorkOrderStatusReason",
                ReasonID: reasonId
            });
            if (response) {
                showSuccess("Reason deleted successfully.");
                fetchReasons();
            }
        } catch (error) {
            console.error("Failed to delete reason", error);
        } finally {
            setDeleteId('');
            setOpenDeleteModal(false);
            setConfirmAction(false);
        }
    };

    useEffect(() => {
        if (deleteId && confirmAction) {
            handleDelete(deleteId);
        }
    }, [deleteId, confirmAction]);

    const columns = useMemo(
        () => [
            {
                header: 'Status Category',
                accessorKey: 'StatusCategory',
                enableColumnFilter: false,
                enableSorting: true,
                cell: ({ row }) => row.original.StatusCategory || row.original.statusName || 'N/A'
            },
            {
                header: 'Reason',
                accessorKey: 'ReasonText',
                enableColumnFilter: false,
                enableSorting: true,
                cell: ({ row }) => row.original.ReasonText || 'N/A'
            },
            ...(hasEditPermission || hasDeletePermission ? [{
                header: 'Actions',
                id: 'actions',
                cell: ({ row }) => {
                    return (
                        <div className="d-flex gap-2 ">
                            {hasEditPermission && (
                                <Button color="primary" onClick={() => handleOpenModal(row.original)} className="mr-2">
                                    <FaRegEdit size={18} />
                                </Button>
                            )}
                            {hasDeletePermission && (
                                <Button
                                    color="danger"
                                    onClick={() => { setDeleteId(row.original.ReasonID || row.original.ReasonId || row.original.reasonId || row.original.id); setOpenDeleteModal(true); }}
                                    className="mr-2"
                                >
                                    <MdDeleteOutline size={18} />
                                </Button>
                            )}
                        </div>
                    );
                },
            }] : []),
        ],
        [hasEditPermission, hasDeletePermission]
    );

    const customStyles = {
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        option: (provided, state) => ({
            ...provided,
            color: state.isSelected ? "white" : "black",
            backgroundColor: state.isSelected ? "#0d6efd" : "white",
            "&:hover": {
                backgroundColor: "#f0f0f0",
                color: "black",
            },
        }),
        singleValue: (provided) => ({
            ...provided,
            color: "black",
        }),
    };

    return (
        <React.Fragment>
            <ConfirmationModal
                okText={"Confirm"}
                onCancel={() => { setDeleteId(''); setOpenDeleteModal(false); }}
                onOk={() => { setConfirmAction(true); }}
                isVisible={openDeleteModal}
                title="Delete Reason"
                content="Are you sure you want to delete this reason?"
            />

            <div className="page-content container-fluid">
                <div className="d-flex justify-content-between align-items-center mx-3 mb-4">
                    <Breadcrumbs title="Reasons Master" breadcrumbItems={[{ title: "Dashboard", link: "/dashboard" }, { title: "Reasons Master", link: "#" }]} />
                    {hasAddPermission && (
                        <Button
                            className="bg-primary text-white d-flex justify-content-center gap-1 align-items-center"
                            onClick={() => handleOpenModal()}
                        >
                            <i className="ti-plus"></i> Add Reason
                        </Button>
                    )}
                </div>

                <div className="container-fluid">
                    <TableContainer
                        loading={loading}
                        columns={columns}
                        data={reasons || []}
                        isGlobalFilter={true}
                        isPagination={true}
                        SearchPlaceholder="Search..."
                        pagination="pagination"
                        docName="Reasons"
                        paginationWrapper='dataTables_paginate paging_simple_numbers'
                        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                    />
                </div>
            </div>

            {/* Modal for Adding/Editing Reason */}
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg">
                <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                    {isEditMode ? "Edit Reason" : "Add New Reason"}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="statusSelect">Select Status <span className="text-danger">*</span></Label>
                                    <Select
                                        id="statusSelect"
                                        value={selectedStatus}
                                        onChange={(selectedOption) => setSelectedStatus(selectedOption)}
                                        options={statuses}
                                        placeholder="-- Select Status --"
                                        isClearable={true}
                                        styles={customStyles}
                                        menuPortalTarget={document.body}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="reasonInput">Reason <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        id="reasonInput"
                                        value={newReason}
                                        onChange={(e) => setNewReason(e.target.value)}
                                        placeholder="Enter reason..."
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button color="primary" onClick={handleSaveReason}>{isEditMode ? "Update" : "Save"}</Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default ReasonsMasterList;
